import { ActionMenu,Flex,Space,Tooltip,type ActionMenuItem } from "../ui";
import React from "react";
import { spacing } from "./settingsListDesign";
import { expandSettingsListRenderers, isSettingsListButtonElement, isSettingsListGroupElement } from "./settingsListElementRoles";
import { createFontMeasureContext,measureTextWidth } from "../utils/fontMeasureContext";

/** Icon-Only: Mindestbreite Aktionsspalte (wie SettingsListPage). */
export const ACTION_ICON_ONLY_MIN_WIDTH = 72;
/** Hysterese bei horizontalem Überlauf (wie SettingsListPage). */
export const OVERFLOW_HYSTERESIS_PX = 20;
const ACTION_FULL_SLOT_GAP_PX = 6;        // Space-Abstand zwischen Buttons
const ACTION_CELL_HORIZONTAL_PADDING_PX = 16; // Zell-Padding links+rechts (8+8)
// Pro Text-Button: Icon (14) + Icon-Text-Gap (8) + Button-Padding links/rechts (2+2) = 26.
const ACTION_BUTTON_ICON_TEXT_EXTRA_PX = 26;
const ACTION_BUTTON_TEXT_CHAR_WIDTH_PX = 7.2; // nur noch SSR-Fallback, sonst Canvas (s. u.)
export const ACTION_BUTTON_ICON_ONLY_WIDTH_PX = 32;
const ACTION_BUTTON_WIDTH_SAFETY_PX = 6;  // kleines Sub-Pixel-Polster (Canvas ist exakt)
export const LEGACY_ACTION_SLOT_FALLBACK_COUNT = 2;
/**
 * Höchstzahl sichtbarer Zeilenaktionen (Ronny, 16.10.2026): drei Knöpfe; weitere Aktionen stehen im
 * Menü `⋯` dahinter. Nichts wird weggelassen.
 */
export const SETTINGS_LIST_MAX_ROW_ACTIONS = 3;
const LEGACY_ACTION_BUTTON_FALLBACK_WIDTH_PX = 96;

// Pixelgenaue Label-Messung der Action-Buttons (echte Button-Font, weight 400/14px). Ersetzt
// die char-count-Schätzung, die die Breite systematisch überschätzte → die Aktionsspalte war zu breit
// und nahm Datenspalten (z. B. Name) den Platz weg.
const getButtonMeasureContext = createFontMeasureContext(
  ".settings-list-actions-cell .ck-btn",
  "400"
);

function measureActionLabelWidth(label: string): number {
  return measureTextWidth(getButtonMeasureContext, label, ACTION_BUTTON_TEXT_CHAR_WIDTH_PX);
}

/** Liest Button-Label aus (für Tooltip bei Icon-Only). */
export function getButtonLabel(node: React.ReactNode): string | undefined {
  if (node == null || !React.isValidElement(node)) return undefined;
  const el = node as React.ReactElement<{ children?: React.ReactNode }>;
  const raw = el.props?.children;
  if (typeof raw === "string") return raw.trim() || undefined;
  if (raw != null)
    return React.Children.toArray(raw)
      .filter((c): c is string => typeof c === "string")
      .join("")
      .trim() || undefined;
  return undefined;
}

export function estimateSettingsListActionButtonWidth(label?: string): number {
  if (!label) return LEGACY_ACTION_BUTTON_FALLBACK_WIDTH_PX;
  return Math.max(
    ACTION_BUTTON_ICON_ONLY_WIDTH_PX,
    measureActionLabelWidth(label) + ACTION_BUTTON_ICON_TEXT_EXTRA_PX
  );
}

export function estimateSettingsListActionsWidth(buttonWidths: number[], slotCount: number): number {
  const normalizedSlotCount = Math.max(1, slotCount);
  const contentWidth =
    buttonWidths.length > 0
      ? buttonWidths.reduce((sum, width) => sum + width, 0)
      : normalizedSlotCount * LEGACY_ACTION_BUTTON_FALLBACK_WIDTH_PX;
  return (
    contentWidth +
    Math.max(0, normalizedSlotCount - 1) * ACTION_FULL_SLOT_GAP_PX +
    ACTION_CELL_HORIZONTAL_PADDING_PX +
    ACTION_BUTTON_WIDTH_SAFETY_PX
  );
}

export function estimateSettingsListIconActionsWidth(slotCount: number): number {
  const normalizedSlotCount = Math.max(1, slotCount);
  return Math.max(
    ACTION_ICON_ONLY_MIN_WIDTH,
    normalizedSlotCount * ACTION_BUTTON_ICON_ONLY_WIDTH_PX +
      Math.max(0, normalizedSlotCount - 1) * ACTION_FULL_SLOT_GAP_PX +
      ACTION_CELL_HORIZONTAL_PADDING_PX
  );
}

export function collectSettingsListActionButtonLabels(node: React.ReactNode, labels: string[] = []): string[] {
  if (node == null || typeof node === "boolean") return labels;
  if (Array.isArray(node)) {
    node.forEach((child) => collectSettingsListActionButtonLabels(child, labels));
    return labels;
  }
  if (!React.isValidElement(node)) return labels;

  const label = getButtonLabel(node);
  if (label != null) {
    labels.push(label);
    return labels;
  }

  const el = node as React.ReactElement<{ children?: React.ReactNode }>;
  React.Children.forEach(el.props?.children, (child) => collectSettingsListActionButtonLabels(child, labels));
  return labels;
}

export function countSettingsListActionNodes(node: React.ReactNode): number {
  if (node == null || typeof node === "boolean") return 0;
  if (Array.isArray(node)) return node.reduce((sum, child) => sum + countSettingsListActionNodes(child), 0);
  if (!React.isValidElement(node)) return 1;

  const el = node as React.ReactElement<{ children?: React.ReactNode }>;
  if (el.type === React.Fragment || isSettingsListGroupElement(el)) {
    const childCount = React.Children.toArray(el.props?.children).reduce<number>((sum, child) => sum + countSettingsListActionNodes(child), 0);
    return childCount || 1;
  }
  return 1;
}

/** Rendert Button-Elemente mit Icon ohne Text (für schmale Viewports / Überlauf). */
export function withIconOnlyActions(node: React.ReactNode): React.ReactNode {
  if (node == null) return node;
  if (!React.isValidElement(node)) return node;
  const el = node as React.ReactElement<{ icon?: React.ReactNode; children?: React.ReactNode }>;
  if (isSettingsListButtonElement(el) && el.props.icon != null) {
    const label = getButtonLabel(node);
    return React.cloneElement(el, {
      children: null,
      "aria-label": label,
      title: label
    } as Partial<typeof el.props>);
  }
  if (el.props?.children != null) {
    const children = React.Children.map(el.props.children, withIconOnlyActions);
    return React.cloneElement(el, {}, children);
  }
  return node;
}

function flattenActionNode(node: React.ReactNode): React.ReactNode[] {
  if (node == null) return [];
  if (!React.isValidElement(node)) return [node];
  const el = node as React.ReactElement<{ children?: React.ReactNode }>;
  const isFlattenWrapper = el.type === React.Fragment || isSettingsListGroupElement(el);
  if (!isFlattenWrapper) return [node];
  const children = React.Children.toArray(el.props?.children ?? null);
  if (children.length === 0) return [node];
  return children.flatMap((child) => flattenActionNode(child));
}

/**
 * Vereinheitlicht Actions aus unterschiedlichen Render-Formen:
 * - Fragments/Space-Wrapper werden aufgelöst
 * - optional auf erwartete Action-Anzahl begrenzt (z. B. slotCount)
 */
export function normalizeSettingsListActionItems(items: React.ReactNode[], expectedCount?: number): React.ReactNode[] {
  const flattened = items.flatMap((item) => flattenActionNode(item));
  const normalized = flattened.length > 0 ? flattened : items;
  if (expectedCount != null && expectedCount > 0 && normalized.length > expectedCount) {
    return limitSettingsListRowActions(normalized.slice(0, expectedCount));
  }
  return limitSettingsListRowActions(normalized);
}

function isActionPlaceholder(node: React.ReactNode): boolean {
  if (node == null || typeof node === "boolean") return true;
  if (!React.isValidElement(node)) return false;
  const props = node.props as { style?: React.CSSProperties; "aria-hidden"?: unknown };
  return props.style?.visibility === "hidden" || props["aria-hidden"] === true;
}

/** Knopf einer Aktion (auch hinter Tooltip oder reinem Darstellungsbaustein) als Menüeintrag. */
function actionMenuItem(node: React.ReactNode, key: string): ActionMenuItem | null {
  const button = findActionButton(expandSettingsListRenderers(node));
  if (!button) return null;
  const props = button.props as { onClick?: (event: React.SyntheticEvent<HTMLElement>) => void; icon?: React.ReactNode; danger?: boolean; disabled?: boolean; "aria-label"?: string };
  const label = getButtonLabel(button) ?? props["aria-label"];
  if (!label || !props.onClick) return null;
  return { key, label, icon: props.icon, danger: props.danger, disabled: props.disabled, onClick: ({ domEvent }) => props.onClick?.(domEvent) };
}

function findActionButton(node: React.ReactNode): React.ReactElement | null {
  if (!React.isValidElement(node)) return null;
  if (isSettingsListButtonElement(node)) return node;
  const children = React.Children.toArray((node.props as { children?: React.ReactNode }).children);
  // Nur einfache Hüllen (Tooltip, Fragment) durchsuchen; mehrere Kinder sind keine einzelne Aktion.
  return children.length === 1 ? findActionButton(children[0]) : null;
}

/**
 * Drei Knöpfe je Zeile, weitere Aktionen im Menü `⋯` dahinter. Überzählige Platzhalter fallen weg.
 * Bausteine mit eigenem Zustand (kein einfacher Knopf) bleiben sichtbar, weil ihr Klick aus einem
 * Menü heraus nichts auslösen würde; sie gehören deshalb in die ersten drei.
 */
export function limitSettingsListRowActions(items: React.ReactNode[]): React.ReactNode[] {
  if (items.length <= SETTINGS_LIST_MAX_ROW_ACTIONS) return items;
  const real = items.filter((item) => !isActionPlaceholder(item));
  if (real.length <= SETTINGS_LIST_MAX_ROW_ACTIONS) {
    const kept = [...items];
    for (let index = kept.length - 1; index >= 0 && kept.length > SETTINGS_LIST_MAX_ROW_ACTIONS; index--) {
      if (isActionPlaceholder(kept[index])) kept.splice(index, 1);
    }
    return kept;
  }
  const visible: React.ReactNode[] = [];
  const overflow: ActionMenuItem[] = [];
  real.forEach((item, index) => {
    const menuItem = visible.length >= SETTINGS_LIST_MAX_ROW_ACTIONS ? actionMenuItem(item, `overflow-${index}`) : null;
    if (menuItem) overflow.push(menuItem); else visible.push(item);
  });
  return overflow.length ? [...visible, <ActionMenu key="overflow" type="text" size="small" items={overflow} />] : visible;
}

/** Standard: eine Zeile wie SettingsListPage. `vertical`: gestapelte Aktionen, rechts ausgerichtet (z. B. Personen-Vertragspaket + Unterverträge). */
export type SettingsListActionCellLayout = "horizontal" | "vertical";

/**
 * Aktions-Zelle wie in SettingsListPage: Space, optional Icon-only + Tooltip.
 * @param layout horizontal = eine Zeile; vertical = übereinander, `alignItems: flex-end` (gleiche Slot-Höhen wie mit Platzhalter-Buttons).
 */
export function renderSettingsListStyleActionCell(
  withPlaceholders: React.ReactNode[],
  useIconOnlyInTable: boolean,
  layout: SettingsListActionCellLayout = "horizontal"
): React.ReactElement {
  const items = useIconOnlyInTable
    ? withPlaceholders.map((x, i) => {
        if (x == null || !React.isValidElement(x)) return <React.Fragment key={i}>{x}</React.Fragment>;
        const label = getButtonLabel(x);
        const iconOnly = withIconOnlyActions(x);
        return label != null ? (
          <Tooltip key={i} title={label}>
            {iconOnly}
          </Tooltip>
        ) : (
          <React.Fragment key={i}>{iconOnly}</React.Fragment>
        );
      })
    : withPlaceholders;
  if (layout === "vertical") {
    const rowNodes = Array.isArray(items) ? items : React.Children.toArray(items);
    return (
      <div className="settings-list-actions-stack">
        {rowNodes.map((node, i) => (
          <div key={i} className="settings-list-actions-stack-row">
            {node}
          </div>
        ))}
      </div>
    );
  }
  return (
    <Space size="small" wrap={false}>
      {items}
    </Space>
  );
}

export function renderSettingsListCardActionRow(
  items: React.ReactNode[],
  useIconOnly: boolean,
  borderColor: string,
  keyPrefix = "card-action"
): React.ReactNode {
  const normalized = normalizeSettingsListActionItems(items).filter((item) => item != null);
  if (normalized.length === 0) return null;

  const renderedItems = useIconOnly
    ? normalized.map((item, index) => {
        if (!React.isValidElement(item)) return <React.Fragment key={`${keyPrefix}-${index}`}>{item}</React.Fragment>;
        const label = getButtonLabel(item);
        const iconOnly = withIconOnlyActions(item);
        const labeledIconOnly = React.isValidElement(iconOnly)
          ? React.cloneElement(iconOnly as React.ReactElement<Record<string, unknown>>, { "aria-label": label, title: label })
          : iconOnly;
        return label ? (
          <Tooltip key={`${keyPrefix}-${index}`} title={label}>
            {labeledIconOnly}
          </Tooltip>
        ) : (
          <React.Fragment key={`${keyPrefix}-${index}`}>{labeledIconOnly}</React.Fragment>
        );
      })
    : normalized.map((item, index) => (
        <React.Fragment key={`${keyPrefix}-${index}`}>
          {item}
        </React.Fragment>
      ));

  return (
    <Flex
      className={`settings-list-card-actions-row ${useIconOnly ? "settings-list-card-actions-row-mobile" : "settings-list-card-actions-row-desktop"}`}
      justify="flex-end"
      align="center"
      gap={8}
      wrap="wrap"
      style={{ width: "100%", marginTop: spacing.xs, paddingTop: spacing.sm, borderTop: `1px solid ${borderColor}` }}
    >
      {renderedItems}
    </Flex>
  );
}
